/**
 * The specification field registry, and the structural types the read-side
 * works in.
 *
 * The types here are deliberately `__typename`-agnostic: the same shape arrives
 * as `ProductSpecifications` from the catalogue and as
 * `OrderProductSpecifications` from a quote line's frozen snapshot, and both
 * have to render through one component.
 */
import { ProductDecorationMethod } from '@codezo/graphql';

/* ════════════════════════ STRUCTURAL TYPES ════════════════════════ */

export interface SpecMeasureLike {
	value: number;
	unit: string;
}

export interface SpecDimensionsLike {
	length?: number | null;
	width?: number | null;
	height?: number | null;
	unit?: string | null;
}

export interface SpecColorLike {
	name?: string | null;
	code?: string | null;
	pantone?: string | null;
	appliesTo?: string | null;
	finish?: string | null;
	isPrimary?: boolean | null;
}

export interface SpecPrintAreaLike {
	width?: number | null;
	height?: number | null;
	unit?: string | null;
}

export interface SpecDecorationLike {
	id: string;
	/** A `ProductDecorationMethod` value; the snapshot enum mirrors it exactly. */
	method: string;
	methodNote?: string | null;
	placement?: string | null;
	colorCount?: number | null;
	area?: SpecPrintAreaLike | null;
	colors?: readonly SpecColorLike[] | null;
	artworkUrl?: string | null;
	notes?: string | null;
	sortOrder?: number | null;
}

export interface SpecCustomLike {
	id: string;
	label: string;
	value: string;
	unit?: string | null;
	group?: string | null;
	sortOrder?: number | null;
}

export interface SpecPackagingLike {
	type?: string | null;
	dimensions?: SpecDimensionsLike | null;
	weight?: number | null;
	weightUnit?: string | null;
	notes?: string | null;
}

export interface SpecificationsLike {
	material?: string | null;
	secondaryMaterials?: readonly string[] | null;
	finish?: string | null;
	textureDescription?: string | null;
	features?: readonly string[] | null;
	capacity?: SpecMeasureLike | null;
	colors?: readonly SpecColorLike[] | null;
	packaging?: SpecPackagingLike | null;
	decorations?: readonly SpecDecorationLike[] | null;
	custom?: readonly SpecCustomLike[] | null;
}

/* ════════════════════════ FIELD REGISTRY ════════════════════════ */

export type SpecFieldKind =
	| 'text'
	| 'longtext'
	| 'tags'
	| 'number'
	| 'integer'
	| 'measure'
	| 'dimensions';

export type SpecFieldGroup = 'material' | 'packaging';

export interface SpecFieldDef {
	/** Stable key, also the path into `ProductSpecificationsInput`. */
	key: string;
	label: string;
	kind: SpecFieldKind;
	group: SpecFieldGroup;
	placeholder?: string;
	/** Selectable units for `measure` / `dimensions` fields. */
	units?: readonly string[];
	defaultUnit?: string;
	/** Suffix shown after a plain `number` field. */
	suffix?: string;
	maxLength?: number;
}

const LENGTH_UNITS = ['cm', 'mm', 'in', 'm'] as const;

export const SPEC_FIELDS: readonly SpecFieldDef[] = [
	{
		key: 'material',
		label: 'Material',
		kind: 'text',
		group: 'material',
		placeholder: 'e.g. 12 oz organic cotton canvas · 340 gsm',
		maxLength: 200,
	},
	{
		key: 'secondaryMaterials',
		label: 'Secondary materials',
		kind: 'tags',
		group: 'material',
		placeholder: 'Add a material and press Enter',
	},
	{
		key: 'finish',
		label: 'Finish',
		kind: 'text',
		group: 'material',
		placeholder: 'e.g. matte, brushed, powder coated',
		maxLength: 120,
	},
	{
		key: 'textureDescription',
		label: 'Texture',
		kind: 'longtext',
		group: 'material',
		placeholder: 'Surface detail worth calling out',
		maxLength: 500,
	},
	{
		key: 'features',
		label: 'Features',
		kind: 'tags',
		group: 'material',
		placeholder: 'e.g. dishwasher safe',
	},
	{
		key: 'capacity',
		label: 'Capacity',
		kind: 'measure',
		group: 'material',
		units: ['ml', 'l', 'oz', 'g'],
		defaultUnit: 'ml',
	},
	{
		key: 'packaging.type',
		label: 'Packaging',
		kind: 'text',
		group: 'packaging',
		placeholder: 'e.g. polybag, gift box, sleeve',
		maxLength: 120,
	},
	{
		key: 'packaging.dimensions',
		label: 'Packed size',
		kind: 'dimensions',
		group: 'packaging',
		units: LENGTH_UNITS,
		defaultUnit: 'cm',
	},
	{
		key: 'packaging.weight',
		label: 'Packed weight',
		kind: 'measure',
		group: 'packaging',
		units: ['g', 'kg', 'lb'],
		defaultUnit: 'g',
	},
	{
		key: 'packaging.notes',
		label: 'Packaging notes',
		kind: 'longtext',
		group: 'packaging',
		placeholder: 'Anything the packer needs to know',
		maxLength: 500,
	},
];

const FIELD_BY_KEY = new Map(SPEC_FIELDS.map((f) => [f.key, f]));

export const getSpecField = (key: string) => FIELD_BY_KEY.get(key);

export const DECORATION_METHOD_LABELS: Record<ProductDecorationMethod, string> = {
	[ProductDecorationMethod.Debossing]: 'Debossing',
	[ProductDecorationMethod.DigitalPrint]: 'Digital print',
	[ProductDecorationMethod.Doming]: 'Doming',
	[ProductDecorationMethod.Dtg]: 'DTG',
	[ProductDecorationMethod.Embossing]: 'Embossing',
	[ProductDecorationMethod.Embroidery]: 'Embroidery',
	[ProductDecorationMethod.Engraving]: 'Engraving',
	[ProductDecorationMethod.HeatTransfer]: 'Heat transfer',
	[ProductDecorationMethod.HotFoil]: 'Hot foil',
	[ProductDecorationMethod.LaserEngraving]: 'Laser engraving',
	[ProductDecorationMethod.Other]: 'Other',
	[ProductDecorationMethod.PadPrint]: 'Pad print',
	[ProductDecorationMethod.ScreenPrint]: 'Screen print',
	[ProductDecorationMethod.Sticker]: 'Sticker',
	[ProductDecorationMethod.Sublimation]: 'Sublimation',
	[ProductDecorationMethod.UvPrint]: 'UV print',
	[ProductDecorationMethod.WovenLabel]: 'Woven label',
};

export const DECORATION_METHODS = Object.values(ProductDecorationMethod).sort((a, b) =>
	DECORATION_METHOD_LABELS[a].localeCompare(DECORATION_METHOD_LABELS[b])
);

/** Snapshot lines carry the mirrored enum, so fall back to the raw value. */
export const decorationMethodLabel = (method: string) =>
	DECORATION_METHOD_LABELS[method as ProductDecorationMethod] ?? method;
